import React, { useEffect, useMemo, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { API_BASE } from "../../constants/api";

const TENANTS = gql`
  query {
    getAllTentants {
      _id
      firstName
      lastName
      email
      adminId
    }
  }
`;

const GET_SMS_WALLET_BALANCE = gql`
  query getSmsWalletBalance($tenantId: String!) {
    getSmsWalletBalance(tenantId: $tenantId) {
      tenantId
      balanceUnits
    }
  }
`;

// Both meters in one round trip (via aliases) rather than a second query per
// tenant on top of GET_SMS_WALLET_BALANCE, so the per-tenant fan-out below
// stays at 2 requests/tenant instead of growing to 3.
const GET_TENANT_USAGE_SMS_AND_EMAIL = gql`
  query getTenantUsageSmsAndEmail($tenantId: String!, $fromDay: String, $toDay: String) {
    sms: getUsageDetails(tenantId: $tenantId, meter: "sms", fromDay: $fromDay, toDay: $toDay) {
      totalOutbound
      totalInbound
    }
    email: getUsageDetails(tenantId: $tenantId, meter: "email", fromDay: $fromDay, toDay: $toDay) {
      totalOutbound
      totalInbound
    }
  }
`;


const toUtcDay = (date = new Date()) => date.toISOString().slice(0, 10);
const addDaysUtc = (date, days) => new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

const CHART_COLORS = {
  outbound: "#6366f1",
  inbound: "#22c55e",
  credits: "#0ea5e9",
};

function truncateLabel(s, max = 20) {
  if (!s) return "—";
  const t = String(s).trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function SummaryTile({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: (t) =>
          `linear-gradient(135deg, ${t.palette.grey[50]} 0%, ${t.palette.common.white} 100%)`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.4}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: color || "text.primary" }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => (color ? alpha(color, 0.14) : theme.palette.action.hover),
              color: color || "text.secondary",
            }}
          >
            <Icon fontSize="small" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function BillingDashboard() {
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const { data, loading, error } = useQuery(TENANTS, { fetchPolicy: "network-only" });

  const toDay = toUtcDay();
  const fromDay = toUtcDay(addDaysUtc(new Date(), -29));

  const tenants = useMemo(() => data?.getAllTentants || [], [data]);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [stripeOverview, setStripeOverview] = useState(null);
  const [stripeOverviewLoading, setStripeOverviewLoading] = useState(false);
  const [stripeOverviewError, setStripeOverviewError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadStripeOverview() {
      setStripeOverviewLoading(true);
      setStripeOverviewError(null);
      try {
        const r = await fetch(
          `${API_BASE}/admin/stripe/all-tenants-overview?listLimit=8&concurrency=8&meterLatest=5`,
          { credentials: "include" }
        );
        const j = await r.json();
        if (!r.ok || j.success === false) {
          throw new Error(j.error || "Failed to load Stripe overview");
        }
        if (!cancelled) setStripeOverview(j);
      } catch (e) {
        if (!cancelled) {
          setStripeOverviewError(e.message);
          setStripeOverview(null);
        }
      } finally {
        if (!cancelled) setStripeOverviewLoading(false);
      }
    }
    loadStripeOverview();
    return () => {
      cancelled = true;
    };
  }, []);

  const stripeByAdminId = useMemo(() => {
    const m = {};
    (stripeOverview?.tenants || []).forEach((t) => {
      if (t?.adminId) m[String(t.adminId)] = t;
    });
    return m;
  }, [stripeOverview]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!tenants.length) {
        setRows([]);
        return;
      }

      setRowsLoading(true);
      try {
        const results = await Promise.all(
          tenants.map(async (t) => {
            const tenantId = t.adminId || t._id;

            const [walletRes, usageRes] = await Promise.all([
              apollo.query({
                query: GET_SMS_WALLET_BALANCE,
                variables: { tenantId },
                fetchPolicy: "network-only",
              }),
              apollo.query({
                query: GET_TENANT_USAGE_SMS_AND_EMAIL,
                variables: { tenantId, fromDay, toDay },
                fetchPolicy: "network-only",
              }),
            ]);

            const balanceUnits = walletRes?.data?.getSmsWalletBalance?.balanceUnits ?? 0;
            const smsUsage = usageRes?.data?.sms;
            const emailUsage = usageRes?.data?.email;

            return {
              _id: t._id,
              adminId: t.adminId,
              name: `${t.firstName || ""} ${t.lastName || ""}`.trim() || t.email || t._id,
              email: t.email,
              balanceUnits,
              outbound30d: smsUsage?.totalOutbound ?? 0,
              inbound30d: smsUsage?.totalInbound ?? 0,
              emailOutbound30d: emailUsage?.totalOutbound ?? 0,
              emailInbound30d: emailUsage?.totalInbound ?? 0,
            };
          })
        );

        if (!cancelled) setRows(results);
      } catch (e) {
        console.error("Failed to load billing dashboard rows", e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setRowsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apollo, tenants, fromDay, toDay]);

  const smsAggregatePie = useMemo(() => {
    const outbound = rows.reduce((s, r) => s + (Number(r.outbound30d) || 0), 0);
    const inbound = rows.reduce((s, r) => s + (Number(r.inbound30d) || 0), 0);
    return [
      { name: "Outbound (30d)", value: outbound },
      { name: "Inbound (30d)", value: inbound },
    ].filter((d) => d.value > 0);
  }, [rows]);

  const smsByTenantBars = useMemo(() => {
    return [...rows]
      .sort((a, b) => b.outbound30d + b.inbound30d - (a.outbound30d + a.inbound30d))
      .slice(0, 12)
      .map((r) => ({
        name: truncateLabel(r.name || r.email || r._id, 18),
        outbound: r.outbound30d,
        inbound: r.inbound30d,
      }));
  }, [rows]);

  const creditsByTenantBars = useMemo(() => {
    return [...rows]
      .sort((a, b) => b.balanceUnits - a.balanceUnits)
      .slice(0, 12)
      .map((r) => ({
        name: truncateLabel(r.name || r.email || r._id, 14),
        credits: r.balanceUnits,
      }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const aid = String(r.adminId || r._id || "");
      const st = stripeByAdminId[aid];
      const haystack = [r.name, r.email, r.adminId, r._id, st?.stripeCustomerId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [rows, search, stripeByAdminId]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        credits: acc.credits + (Number(r.balanceUnits) || 0),
        out: acc.out + (Number(r.outbound30d) || 0),
        in: acc.in + (Number(r.inbound30d) || 0),
      }),
      { credits: 0, out: 0, in: 0 }
    );
  }, [rows]);

  const pageBusy = loading || rowsLoading;

  return (
    <Box sx={{ pb: 4 }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} letterSpacing="-0.02em">
          Billing
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={720}>
          SMS wallet balances, last 30 days of SMS traffic (UTC), and Stripe signals per tenant. Click a row to open
          tenant details.
        </Typography>
      </Stack>

      {stripeOverviewLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {stripeOverviewError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stripe overview: {stripeOverviewError}
        </Alert>
      )}
      {stripeOverview && !stripeOverviewLoading && !stripeOverviewError && (
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          Stripe linked <strong>{stripeOverview.tenantCount}</strong> tenant(s) · fetched in{" "}
          <strong>{stripeOverview.durationMs}</strong> ms
        </Alert>
      )}

      {!pageBusy && !error && rows.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryTile
              title="Tenants"
              value={rows.length}
              subtitle="In this workspace"
              icon={PeopleRoundedIcon}
              color="#0d9488"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryTile
              title="SMS credits (sum)"
              value={totals.credits.toLocaleString()}
              subtitle="Wallet units across tenants"
              icon={AccountBalanceWalletRoundedIcon}
              color="#0ea5e9"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryTile
              title="Outbound (30d)"
              value={totals.out.toLocaleString()}
              subtitle="Aggregate sent"
              icon={TrendingUpRoundedIcon}
              color="#6366f1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryTile
              title="Inbound (30d)"
              value={totals.in.toLocaleString()}
              subtitle="Aggregate received"
              icon={SmsRoundedIcon}
              color="#22c55e"
            />
          </Grid>
        </Grid>
      )}

      {!loading && !rowsLoading && !error && rows.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  All tenants — SMS volume (30d)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Share of outbound vs inbound
                </Typography>
                <Box sx={{ width: "100%", height: 280 }}>
                  {smsAggregatePie.length === 0 ? (
                    <Typography color="text.secondary" variant="body2" sx={{ py: 4, textAlign: "center" }}>
                      No SMS usage in this period.
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={smsAggregatePie}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={56}
                          outerRadius={92}
                          paddingAngle={2}
                        >
                          {smsAggregatePie.map((entry, i) => (
                            <Cell
                              key={entry.name}
                              fill={i === 0 ? CHART_COLORS.outbound : CHART_COLORS.inbound}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [v, "Messages"]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={8}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Top tenants by SMS traffic
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Outbound vs inbound (top 12)
                </Typography>
                <Box sx={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={smsByTenantBars} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        height={72}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="outbound" name="Outbound" fill={CHART_COLORS.outbound} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="inbound" name="Inbound" fill={CHART_COLORS.inbound} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Top tenants by SMS credits left
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Horizontal ranking (top 12)
                </Typography>
                <Box sx={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={creditsByTenantBars}
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [v, "Credits"]} />
                      <Bar dataKey="credits" name="Credits left" fill={CHART_COLORS.credits} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        {pageBusy ? (
          <Box sx={{ p: 4 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading tenant billing data…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load tenants
          </Alert>
        ) : (
          <>
            <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                <TextField
                  size="small"
                  placeholder="Search by name, email, adminId, Stripe customer id…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { xs: "100%", sm: 360 }, maxWidth: 520 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> tenants
                </Typography>
              </Stack>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, maxHeight: { md: "min(70vh, 720px)" } }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1080 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">SMS Out (30d)</TableCell>
                    <TableCell align="right">SMS In (30d)</TableCell>
                    <TableCell align="right">Email Out (30d)</TableCell>
                    <TableCell align="right">Email In (30d)</TableCell>
                    <TableCell>Stripe customer</TableCell>
                    <TableCell align="center">Subs</TableCell>
                    <TableCell align="center">Pay intents</TableCell>
                    <TableCell align="center">Invoices</TableCell>
                    <TableCell align="center">Meter</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length > 0 ? (
                    filteredRows.map((r) => {
                      const aid = String(r.adminId || r._id);
                      const st = stripeByAdminId[aid];
                      return (
                        <TableRow
                          key={r._id}
                          hover
                          sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
                          onClick={() => navigate(`/tenant-details/${r._id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {r.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {r.email}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{r.balanceUnits}</TableCell>
                          <TableCell align="right">{r.outbound30d}</TableCell>
                          <TableCell align="right">{r.inbound30d}</TableCell>
                          <TableCell align="right">{r.emailOutbound30d}</TableCell>
                          <TableCell align="right">{r.emailInbound30d}</TableCell>
                          <TableCell sx={{ maxWidth: 160 }}>
                            {st?.error ? (
                              <Chip size="small" label="Error" color="error" title={st.error} />
                            ) : st?.stripeCustomerId ? (
                              <Typography variant="caption" noWrap display="block" title={st.stripeCustomerId}>
                                {st.stripeCustomerId}
                              </Typography>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="center">{st?.subscriptions?.length ?? "—"}</TableCell>
                          <TableCell align="center">{st?.paymentIntents?.length ?? "—"}</TableCell>
                          <TableCell align="center">{st?.invoices?.length ?? "—"}</TableCell>
                          <TableCell align="center">
                            {st?.meter?.totalUnits != null ? st.meter.totalUnits : "—"}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/tenant-details/${r._id}`);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                bgcolor: "#A1368B",
                                "&:hover": { bgcolor: "#8b2d78" },
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                          {search.trim() ? "No search results" : "No tenants found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Card>
    </Box>
  );
}
